/**
 * Getting off the county's flat frame and back onto the round earth.
 *
 * A baked county is a local plane in meters: the projection records which UTM
 * zone it came from and where its origin sits, and everything after that is
 * plain geometry. That is the right shape for a simulation and the wrong shape
 * for anything that arrives in latitude and longitude -- which, so far, is
 * exactly one thing: satellite imagery, which is published as Web Mercator
 * tiles and has never heard of Waukesha's UTM origin.
 *
 * So this is the inverse of `mapgen.geo.to_utm`, and the same series expansion
 * read backwards. It is verified by round-tripping against the bake rather than
 * against a paper: the county file is the only thing that has to agree with it.
 */

// WGS84, matching mapgen/geo.py exactly. Two copies of these constants is one
// more than ideal, and the alternative is the renderer importing Python.
const A = 6378137.0;
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
const EP2 = E2 / (1 - E2);
const K0 = 0.9996;
const FALSE_EASTING = 500000.0;

export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * UTM easting and northing to WGS84 degrees, northern hemisphere.
 *
 * The zone is passed rather than derived for the same reason the forward
 * transform takes it: a county that straddles a zone boundary is still one
 * flat piece of ground, and forcing every point into one zone keeps it
 * continuous.
 */
export function fromUtm(easting: number, northing: number, zone: number): LatLon {
  const x = easting - FALSE_EASTING;
  const m = northing / K0;

  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));
  const mu = m / (A * (1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 * E2 * E2) / 256));

  // Footpoint latitude: the latitude whose meridian arc is this northing.
  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
    ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
    ((151 * e1 ** 3) / 96) * Math.sin(6 * mu) +
    ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);

  const sin1 = Math.sin(phi1);
  const cos1 = Math.cos(phi1);
  const tan1 = Math.tan(phi1);

  const c1 = EP2 * cos1 * cos1;
  const t1 = tan1 * tan1;
  const n1 = A / Math.sqrt(1 - E2 * sin1 * sin1);
  const r1 = (A * (1 - E2)) / (1 - E2 * sin1 * sin1) ** 1.5;
  const d = x / (n1 * K0);

  const lat =
    phi1 -
    ((n1 * tan1) / r1) *
      ((d * d) / 2 -
        ((5 + 3 * t1 + 10 * c1 - 4 * c1 * c1 - 9 * EP2) * d ** 4) / 24 +
        ((61 + 90 * t1 + 298 * c1 + 45 * t1 * t1 - 252 * EP2 - 3 * c1 * c1) * d ** 6) / 720);

  const lon =
    (d -
      ((1 + 2 * t1 + c1) * d ** 3) / 6 +
      ((5 - 2 * c1 + 28 * t1 - 3 * c1 * c1 + 8 * EP2 + 24 * t1 * t1) * d ** 5) / 120) /
    cos1;

  const lambda0 = (((zone - 1) * 6 - 180 + 3) * Math.PI) / 180;
  return {
    lat: (lat * 180) / Math.PI,
    lon: ((lambda0 + lon) * 180) / Math.PI,
  };
}

/** WGS84 degrees to UTM, the forward transform, mirroring mapgen/geo.py. */
export function toUtm(lat: number, lon: number, zone: number): { easting: number; northing: number } {
  const phi = (lat * Math.PI) / 180;
  const lambda = (lon * Math.PI) / 180;
  const lambda0 = (((zone - 1) * 6 - 180 + 3) * Math.PI) / 180;

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = Math.tan(phi);

  const n = A / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const t = tanPhi * tanPhi;
  const c = EP2 * cosPhi * cosPhi;
  const a = (lambda - lambda0) * cosPhi;
  const m =
    A *
    ((1 - E2 / 4 - (3 * E2 ** 2) / 64 - (5 * E2 ** 3) / 256) * phi -
      ((3 * E2) / 8 + (3 * E2 ** 2) / 32 + (45 * E2 ** 3) / 1024) * Math.sin(2 * phi) +
      ((15 * E2 ** 2) / 256 + (45 * E2 ** 3) / 1024) * Math.sin(4 * phi) -
      ((35 * E2 ** 3) / 3072) * Math.sin(6 * phi));

  return {
    easting:
      K0 *
        n *
        (a + ((1 - t + c) * a ** 3) / 6 + ((5 - 18 * t + t * t + 72 * c - 58 * EP2) * a ** 5) / 120) +
      FALSE_EASTING,
    northing:
      K0 *
      (m +
        n *
          tanPhi *
          ((a * a) / 2 +
            ((5 - t + 9 * c + 4 * c * c) * a ** 4) / 24 +
            ((61 - 58 * t + t * t + 600 * c - 330 * EP2) * a ** 6) / 720)),
  };
}

/**
 * Web Mercator tile coordinates, which is what every tile server speaks.
 *
 * Fractional on purpose: the caller wants both which tile a point is in and
 * where within it, and rounding here would throw the second away.
 */
export function tileOf(lat: number, lon: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom;
  const phi = (lat * Math.PI) / 180;
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2) * n,
  };
}

/** The north-west corner of a tile, in degrees. */
export function tileCorner(x: number, y: number, zoom: number): LatLon {
  const n = 2 ** zoom;
  const lonDeg = (x / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
  return { lat: (latRad * 180) / Math.PI, lon: lonDeg };
}
